import { Component, Inject, Input, LOCALE_ID, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Chart, ChartConfiguration, ChartType } from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels'
import { LinqChartData } from '../linq-chart-data';
import { formatCurrency, formatNumber } from '@angular/common';
import { Currency, DEFAULT_CURRENCY, getCurrency } from '../../common/organisation';
import { OrganisationService } from '../../organisation/organisation.service';
import { abbreviate } from '../bar-chart/bar-chart.component';

// the chart's text, in the app's palette
const INK = '#1E2233'
const SLATE = '#5b6172'
const FONT_FAMILY = "'Montserrat', 'Roboto', system-ui, sans-serif"
// a segment this share of the whole or more carries its share as a label; smaller ones are read from the key or on hover
const LABEL_SHARE = 0.06

@Component({
  selector: 'app-pie-chart',
  templateUrl: 'pie-chart.html',
  styleUrl: 'pie-chart.css',
  standalone: false
})
export class PieChartComponent implements OnInit, OnChanges {
    @Input() data: LinqChartData[];
    @Input() hasComparison:boolean
    @Input() height: number
    @Input() width: number
    @Input() isCurrency:boolean

    // Pie
    public pieChartOptions: ChartConfiguration['options']
    public pieChartType: ChartType = 'doughnut';
    public chartPlugins = [ChartDataLabels];
    public pieChartData:any

    currency:Currency

    constructor(@Inject(LOCALE_ID) public locale: string, organisationService:OrganisationService) {
        // the plugin is given to the chart through [plugins], not registered with Chart (see the bar chart)
        Chart.defaults.set('plugins.datalabels', {
            color: '#000000',
            clip: true,
            display: 'auto',
            anchor: 'center',
            align: 'center',
            formatter: this.formatNumberLabel,
            font: {
                weight: 'bold'
            }
        });

        this.currency = getCurrency(organisationService.organisation?.currencyCode||DEFAULT_CURRENCY)

    }

    ngOnInit(): void {
        this.configure()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["data"] && this.data) {
            this.configure()
            this.setData()
        }
    }

    formatNumberLabel(value:any, context:Context) {
        return formatNumber(Math.round(value), 'en-NZ')
    }

    // the box the chart fills: the size given, or the card's width and a sensible height
    get style() {
        return `position: relative; width: ${this.width ? this.width + 'px' : '100%'}; height: ${this.height || 280}px;`
    }

    private format(value:number, short:boolean) {
        if (this.isCurrency) return short ? this.currency.symbol + abbreviate(value, this.locale) : formatCurrency(value, this.locale, this.currency.symbol, this.currency.code, '1.0-0')
        return short ? abbreviate(value, this.locale) : formatNumber(value, this.locale)
    }

    private share(context:any) {
        const values:number[] = context.dataset.data || []
        const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0)
        return total ? (Number(values[context.dataIndex]) || 0) / total : 0
    }

    configure() {
        this.pieChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '58%',
            layout: { padding: 4 },
            plugins: {
                // the key below the ring, each with its value
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: { color: SLATE, usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 12, font: { family: FONT_FAMILY, size: 12 } },
                },
                tooltip: {
                    backgroundColor: INK,
                    padding: 10,
                    cornerRadius: 6,
                    titleFont: { family: FONT_FAMILY, size: 12, weight: 600 },
                    bodyFont: { family: FONT_FAMILY, size: 12 },
                    callbacks: {
                        title: (items:any[]) => items.map((i:any) => this.data[i.dataIndex]?.name || '').join(', '),
                        label: (context:any) => `${this.format(Number(context.raw) || 0, false)} · ${Math.round(this.share(context) * 100)}%`,
                    },
                },
                // each large enough segment shows its share of the whole, in white on the segment
                datalabels: {
                    color: '#ffffff',
                    anchor: 'center',
                    align: 'center',
                    clip: true,
                    display: (context:any) => this.share(context) >= LABEL_SHARE,
                    formatter: (value:any, context:any) => `${Math.round(this.share(context) * 100)}%`,
                    font: { family: FONT_FAMILY, size: 11, weight: 600 },
                    textStrokeColor: 'rgba(0,0,0,0.25)',
                    textStrokeWidth: 2,
                },
            } as any,
        } as any
    }

    setData() {
        // white gaps between the segments, so neighbouring colours stay distinct
        const ring = () => ({ data: [], backgroundColor: [], borderColor: '#ffffff', borderWidth: 2, hoverOffset: 6 })
        this.pieChartData = {labels:[], datasets:[ring()]}
        if (this.hasComparison) this.pieChartData.datasets.push(ring())

        this.data.forEach((d, i) => {
            if (d) {
                this.pieChartData.labels.push(this.getLegend(d))
                this.pieChartData.datasets[0].data.push(d.value)
                if (d.color) this.pieChartData.datasets[0].backgroundColor.push(d.color)

                if (this.hasComparison) {
                    this.pieChartData.datasets[1].data.push(d.comparison||0)
                    if (d.color) this.pieChartData.datasets[1].backgroundColor.push(d.color)
                }

            }
        })
    }

    getLegend(data:LinqChartData) {
        return data.name + "  " + this.format(data.value || 0, true)
    }}
